import React, { useEffect, useState, useRef } from "react";
import {
  Button as MuiButton,
  Box as MuiBox,
  TextField as MuiTextField,
  CircularProgress,
  Typography as MuiTypography
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import { spacing, SpacingProps } from "@mui/system";
import styled from "@emotion/styled";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import CommonPageLayout from "../../../components/commonPageLayout/commonPageLayout";
import Loader from "../../../components/loader/loader";
import UploadFile from "../../../Utils/uploadFile";
import compressedFile from "../../../Utils/imageCompression";

const Box = styled(MuiBox)`
  display: flex;
  gap: 28px;
  margin-top: 32px;
`;

const TextField = styled(MuiTextField)`
  display: flex;
  padding-top: 1px;
  justify-content: center;
  align-items: center;

  label {
    color: var(--light-text-secondary, rgba(0, 0, 0, 0.6));
    font-feature-settings: "clig" off, "liga" off;
    font-family: "Montserrat", sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 12px;
    letter-spacing: 0.15px;
  }

  input {
    color: var(--light-text-secondary, rgba(0, 0, 0, 0.6));
    font-feature-settings: "clig" off, "liga" off;
    font-family: "Montserrat", sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.15px;
    &::before {
      height: 1px;
      background: rgba(0, 0, 0, 0.42);
    }
    &:hover:not(.Mui-disabled)::before {
      height: 1px;
      background: rgba(0, 0, 0, 0.42);
    }
  }
`;

interface ButtonProps extends SpacingProps {
  component?: React.ElementType;
  to?: string;
  target?: string;
}

const Button = styled(MuiButton)<ButtonProps>(spacing);

const CustomButton: any = styled(MuiButton)`
  margin-top: 30px;
  &.blue_button {
    background-color: blue;
  }

  &.custom_disabled {
    background-color: grey !important;
  }
`;

const LogoContainer = styled(MuiBox)`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 16px;
  margin-bottom: 32px;
`;

const LogoAvatar = styled(MuiBox)`
  width: 100px;
  height: 100px;
  border: 2px solid #e0e0e0;
  border-radius: 50%;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain; /* show full logo without cropping */
    object-position: center;
    border-radius: 50%;
    background: transparent;
  }
`;

const LogoOverlay = styled(MuiBox)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
  border-radius: 50%;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }
`;

const Typography = styled(MuiTypography)``;

interface BusinessProfile {
  BusinessId: string;
  BusinessName: string;
  Street: string;
  PostalCode: string;
  Suburb: string;
  Email: string;
  Logo: string;
  InstagramHandle: string | null;
  PhoneNumber: string;
  WebAddress: string | null;
  Design?: {
    Custom: Array<{
      Color: {
        Primary: string;
        Tertiary: string;
        Secondary: string;
      };
      Product: string;
    }>;
  };
}

function ProfilePage() {
  const navigate = useNavigate();
  let currentBusiness: any = localStorage?.getItem("currentBusiness");
  const state: any = {
    token: localStorage?.getItem("businessAccessToken"),
    currentBusiness: currentBusiness ? JSON.parse(currentBusiness) : null
  };

  const [profileData, setProfileData] = useState<BusinessProfile>({
    BusinessId: "",
    BusinessName: "",
    Street: "",
    PostalCode: "",
    Suburb: "",
    Email: "",
    Logo: "",
    InstagramHandle: "",
    PhoneNumber: "",
    WebAddress: ""
  });

  const [loading, setIsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchProfileData = async () => {
    setIsLoading(true);
    try {
      const config = {
        method: "GET",
        url: `${process.env.REACT_APP_API_BASE_URL}/datacollectionbusiness/businesses`,
        headers: {
          Authorization: "Bearer " + state?.token,
          "Content-Type": "application/json"
        }
      };

      const response = await axios.request(config);
      if (response.data && response.data.length > 0) {
        const currentBusinessId = state?.currentBusiness?.BusinessId;
        const businessProfile = response.data.find(
          (item: any) => item.BusinessId === currentBusinessId
        );
        if (businessProfile) {
          setProfileData(businessProfile);
        }
      }
    } catch (error: any) {
      console.error("Error fetching profile data:", error);
      setErrorMessage(
        error?.response?.data?.errors?.join(", ") ||
          "Failed to fetch profile data"
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, [state?.currentBusiness?.BusinessId]);

  const handleInputChange = (field: keyof BusinessProfile, value: string) => {
    setProfileData((prev) => ({ ...prev, [field]: value }));
    setIsSaveDisabled(false);
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setLogoFile(file);
      setIsSaveDisabled(false);
    }
  };

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let payload = { ...profileData };

      // Handle logo upload if there's a new file
      if (logoFile) {
        const s3LogoBucketName: any = process.env.REACT_APP_BUCKET_NAME;
        const businessIdForKey =
          profileData.BusinessId || state?.currentBusiness?.BusinessId || "";
        const logoCompressedImageFile = await compressedFile(logoFile);
        const logoS3Url = await UploadFile(
          logoCompressedImageFile,
          s3LogoBucketName !== undefined ? s3LogoBucketName : "",
          `business/${businessIdForKey}/logo/${businessIdForKey}_logo_rewardlepay.jpg`,
          s3LogoBucketName
        );
        if (logoS3Url) {
          payload.Logo = logoS3Url;
        }
      }

      const businessIdForUpdate =
        profileData.BusinessId || state?.currentBusiness?.BusinessId || "";

      const config = {
        method: "PUT",
        url: `${process.env.REACT_APP_API_BASE_URL}/datacollection/v1/business/businessauth?businessid=${businessIdForUpdate}`,
        headers: {
          Authorization: "Bearer " + state?.token,
          "Content-Type": "application/json"
        },
        data: payload
      };

      await axios.request(config);
      setSuccessMessage("Profile updated successfully!");
      setErrorMessage(null);
      setIsSaveDisabled(true);
      setLogoFile(null);

      // Refresh data to get updated values
      await fetchProfileData();
    } catch (error: any) {
      console.error("Error updating profile:", error);
      setErrorMessage(
        error?.response?.data?.errors?.join(", ") || "Failed to update profile"
      );
      setSuccessMessage(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <CommonPageLayout
      heading="Account"
      subHeading="Business Profile"
      docs={null}
    >
      {loading ? (
        <Loader />
      ) : (
        <>
          {errorMessage && (
            <Stack sx={{ width: "100%", mb: 2 }} spacing={2}>
              <Alert severity="error">{errorMessage}</Alert>
            </Stack>
          )}

          {successMessage && (
            <Stack sx={{ width: "100%", mb: 2 }} spacing={2}>
              <Alert severity="success">{successMessage}</Alert>
            </Stack>
          )}

          {/* Logo Section */}
          <LogoContainer>
            <Box sx={{ position: "relative", marginTop: "0px !important" }}>
              <LogoAvatar onClick={handleLogoClick}>
                {logoFile || profileData.Logo ? (
                  <img
                    src={
                      logoFile
                        ? URL.createObjectURL(logoFile)
                        : profileData.Logo
                    }
                    alt="Business Logo"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                    }}
                  />
                ) : (
                  <Typography
                    variant="body1"
                    sx={{ color: "#666", fontSize: "14px" }}
                  >
                    LOGO
                  </Typography>
                )}
              </LogoAvatar>
              <LogoOverlay onClick={handleLogoClick}>
                <EditIcon sx={{ color: "white" }} />
              </LogoOverlay>
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              style={{ display: "none" }}
              aria-label="Upload logo"
              title="Upload logo"
            />
          </LogoContainer>

          <Box display={"flex"} flexDirection={"row"}>
            <TextField
              id="business-name-input"
              label="Business Name"
              type="text"
              variant="standard"
              value={profileData.BusinessName}
              onChange={(e) =>
                handleInputChange("BusinessName", e.target.value)
              }
              fullWidth={true}
              size="medium"
              disabled={false}
            />
            <TextField
              id="email-input"
              label="Email"
              type="email"
              variant="standard"
              value={profileData.Email}
              onChange={(e) => handleInputChange("Email", e.target.value)}
              fullWidth={true}
              size="medium"
              disabled={true}
            />
            <TextField
              id="phone-input"
              label="Phone Number"
              type="tel"
              variant="standard"
              value={profileData.PhoneNumber}
              onChange={(e) => handleInputChange("PhoneNumber", e.target.value)}
              fullWidth={true}
              size="medium"
              disabled={false}
            />
          </Box>

          <Box display={"flex"} flexDirection={"row"}>
            <TextField
              id="street-input"
              label="Street Address"
              type="text"
              variant="standard"
              value={profileData.Street}
              onChange={(e) => handleInputChange("Street", e.target.value)}
              fullWidth={true}
              size="medium"
              disabled={false}
            />
            <TextField
              id="suburb-input"
              label="Suburb"
              type="text"
              variant="standard"
              value={profileData.Suburb}
              onChange={(e) => handleInputChange("Suburb", e.target.value)}
              fullWidth={true}
              size="medium"
              disabled={false}
            />
            <TextField
              id="postal-code-input"
              label="Postal Code"
              type="text"
              variant="standard"
              value={profileData.PostalCode}
              onChange={(e) => handleInputChange("PostalCode", e.target.value)}
              fullWidth={true}
              size="medium"
              disabled={false}
            />
          </Box>

          <Box display={"flex"} flexDirection={"row"}>
            <TextField
              id="instagram-input"
              label="Instagram Handle"
              type="text"
              variant="standard"
              value={profileData.InstagramHandle || ""}
              onChange={(e) =>
                handleInputChange("InstagramHandle", e.target.value)
              }
              fullWidth={true}
              size="medium"
              placeholder="@username"
              disabled={false}
            />
            <TextField
              id="website-input"
              label="Website Address"
              type="url"
              variant="standard"
              value={profileData.WebAddress || ""}
              onChange={(e) => handleInputChange("WebAddress", e.target.value)}
              fullWidth={true}
              size="medium"
              placeholder="https://example.com"
              disabled={false}
            />
          </Box>

          <CustomButton
            variant="contained"
            color="secondary"
            mt={4}
            className={
              isSaveDisabled ? "blue_button custom_disabled" : "blue_button"
            }
            onClick={handleSave}
            disabled={saving || isSaveDisabled}
          >
            {saving ? <CircularProgress size={25} color="inherit" /> : "Save"}
          </CustomButton>
        </>
      )}
    </CommonPageLayout>
  );
}

export default ProfilePage;
